import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../lib/axios";
import toast from "react-hot-toast";
import { uppercasePayload } from "../lib/uppercasePayload";

const initialState = {
  getallsales: null,
  pagination: { page: 1, pageSize: 5, totalItems: 0, totalPages: 1 },
  isgetallsales: false,
  iscreatedsales: false,
  editedsales: null,
  searchdata: null,
};

export const CreateSales = createAsyncThunk(
  "sales/createsales",
  async (Category, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "sales",
        uppercasePayload(Category, {
          excludeKeys: ["paymentMethod", "status"],
        }),
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "sales creation failed");
    }
  },
);

export const gettingallSales = createAsyncThunk(
  "sales/getallsales",
  async (
    { page = 1, pageSize = 5, sortDir = "desc" } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.get("sales", {
        params: { page, pageSize, sortDir },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "sales retrieval failed",
      );
    }
  },
);

export const EditSales = createAsyncThunk(
  "sales/updatesales",
  async ({ salesId, updatedData }, { rejectWithValue }) => {
    if (!salesId) {
      toast.error("Invalid Sale ID");
      return rejectWithValue("Invalid Sale ID");
    }

    try {
      const response = await axiosInstance.put(
        `sales/${salesId}`,
        uppercasePayload(updatedData, {
          excludeKeys: ["paymentMethod", "status"],
        }),
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("EditSales Error:", error);
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to update sale. Please try again.",
        },
      );
    }
  },
);

export const DeleteSales = createAsyncThunk(
  "sales/deletesales",
  async (salesId, { rejectWithValue }) => {
    if (!salesId) {
      toast.error("Invalid Sale ID");
      return rejectWithValue("Invalid Sale ID");
    }

    try {
      const response = await axiosInstance.delete(`sales/${salesId}`, {
        withCredentials: true,
      });
      toast.success("Sale deleted successfully");
      return response.data;
    } catch (error) {
      console.error("DeleteSales Error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete sale.";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

export const searchsalesdata = createAsyncThunk(
  "sales/searchdata",
  async (query, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `sales/searchdata?query=${query}`,
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Sales search failed",
      );
    }
  },
);

const salesSlice = createSlice({
  name: "sales",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(gettingallSales.pending, (state) => {
        state.isgetallsales = true;
      })
      .addCase(gettingallSales.fulfilled, (state, action) => {
        state.isgetallsales = false;
        state.getallsales = action.payload.sales;
        state.pagination = action.payload.pagination || state.pagination;
      })

      .addCase(gettingallSales.rejected, (state, action) => {
        state.isgetallsales = false;
        toast.error(action.payload || "Error retrieving sales");
      })

      .addCase(CreateSales.pending, (state) => {
        state.iscreatedsales = true;
      })
      .addCase(CreateSales.fulfilled, (state, action) => {
        state.iscreatedsales = false;
        if (state.getallsales) {
          state.getallsales.push(action.payload.sale); // push sale, not whole response
        }
      })

      .addCase(CreateSales.rejected, (state, action) => {
        state.iscreatedsales = false;
      })

      .addCase(EditSales.fulfilled, (state, action) => {
        state.editedsales = action.payload;
        if (action.payload?.deletedSaleId) {
          const deletedSaleId = action.payload.deletedSaleId;
          const removeFromList = (list) =>
            Array.isArray(list)
              ? list.filter((sale) => String(sale.id) !== String(deletedSaleId))
              : list;
          state.getallsales = removeFromList(state.getallsales);
          state.searchdata = removeFromList(state.searchdata);
          return;
        }
        const updatedSale = action.payload?.sale;
        if (!updatedSale) return;
        const updateList = (list) =>
          Array.isArray(list)
            ? list.map((sale) =>
                String(sale.id) === String(updatedSale.id) ? updatedSale : sale,
              )
            : list;
        state.getallsales = updateList(state.getallsales);
        state.searchdata = updateList(state.searchdata);
      })

      .addCase(EditSales.rejected, (state, action) => {})

      .addCase(DeleteSales.fulfilled, (state, action) => {
        const deletedSaleId = action.payload?.deletedSaleId;
        const removeFromList = (list) =>
          Array.isArray(list)
            ? list.filter((sale) => String(sale.id) !== String(deletedSaleId))
            : list;
        state.getallsales = removeFromList(state.getallsales);
        state.searchdata = removeFromList(state.searchdata);
      })

      .addCase(DeleteSales.rejected, (state, action) => {})

      .addCase(searchsalesdata.fulfilled, (state, action) => {
        state.searchdata = action.payload.sales;
      })

      .addCase(searchsalesdata.rejected, (state, action) => {});
  },
});

export default salesSlice.reducer;
